import {get} from 'lodash';

import {Injectable} from '@angular/core';
import {EventTrackAction, LinkClicked} from '@clients/shared/analytics';
import {Store} from '@ngrx/store';

import {ActivateBlockAction, LoadCompletedBlockAction, LoadIntroBlockAction} from '../+state/blocks/blocks.actions';
import {HideElementAction, HighlightElementAction, ShowElementAction} from '../+state/elements/elements.actions';
import {SurveysCombinedReducerState} from '../+state/interface';
import {RecordResponseAction} from '../+state/responses/responses.actions';
import {ResponsesFacade} from '../+state/responses/responses.facade';
import {ISurveyResponse, SeededSurveyResponse, YesNoSurveyResponse} from '../../models/survey-responses/SurveyResponse.interface';
import {SurveyInstruction, SurveyInstructionType} from '../../models/survey/ISurveyInstruction';
import {instructionShouldRun} from '../utilities/instruction-should-run.function';
import {SurveyElementType, SurveyFacade} from '../../public-api';
import { Dictionary } from '@ngrx/entity';
import { interval, Subscription, takeWhile } from 'rxjs';
import { DoneLoadingAction, LoadingAction } from '@clients/shared/loading';
import { SurveyTrackingAction } from '../+state/survey/survey.actions';
import { BlocksFacade } from '../+state/blocks/blocks.facade';

interface ISurveyInstructionMap {
    [type: string]: (instruction: SurveyInstruction) => void;
}

@Injectable()
export class SurveyInstructionsService {
    private pendingCheckSub?: Subscription;
    /**
     * Handler functions for all of our instruction types.
     */
    private instructionMap: ISurveyInstructionMap = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [SurveyInstructionType.LOAD_INTRO]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new LoadIntroBlockAction()),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [SurveyInstructionType.LOAD_COMPLETE]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new LoadCompletedBlockAction()),
        [SurveyInstructionType.LOAD_BLOCK_BY_ID]: (instruction: SurveyInstruction) =>
            this.loadBlockById(instruction),
        [SurveyInstructionType.OPEN_EXTERNAL_LINK]: (instruction: SurveyInstruction) =>
            this.openExternalLink(instruction),
        [SurveyInstructionType.SET_DATA_PROP]: (instruction: SurveyInstruction) => this.setDataProp(instruction),
        [SurveyInstructionType.SHOW_ELEMENT]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new ShowElementAction(instruction.data)),
        [SurveyInstructionType.DISPLAY_RELATED_QUESTION]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new ShowElementAction(instruction.data)),
        [SurveyInstructionType.HIDE_ELEMENT]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new HideElementAction(instruction.data)),
        [SurveyInstructionType.HIDE_RELATED_QUESTION]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new HideElementAction(instruction.data)),
        [SurveyInstructionType.HIGHLIGHT_ELEMENT]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new HighlightElementAction(instruction.data)),
        [SurveyInstructionType.PRINT_ELEMENT]: (instruction: SurveyInstruction) =>
            this.printElement(instruction),
        [SurveyInstructionType.SHOW_DIALOG]: (instruction: SurveyInstruction) =>
            this.store.dispatch(new ShowElementAction(instruction.data))
    };

    constructor(private store: Store<SurveysCombinedReducerState>, private responsesFacade: ResponsesFacade, private surveyFacade: SurveyFacade, private blocksFacade: BlocksFacade) {}

    public process(instructions: SurveyInstruction[], invokingElementId: string | number = null, responseParam?: Dictionary<ISurveyResponse>): void {
        if (!instructions || !instructions.length) return;

        const responsesEntities = this.responsesFacade.getEntities();
        const response = responseParam ? responseParam : responsesEntities;

        instructions
            .filter((instruction: SurveyInstruction) => instructionShouldRun(instruction, response, invokingElementId))
            .forEach((instruction: SurveyInstruction) => this.processInstruction(instruction));
    }

    private processInstruction(instruction: SurveyInstruction): void {
        if (!instruction) throw new Error(`Expected object of type SurveyInstruction and receive ${instruction}`);
        if (!this.instructionMap[instruction.type]) throw new Error(`Invalid instruction type: ${instruction.type}`);

        this.instructionMap[instruction.type](instruction);
    }

    private setDataProp(instruction: SurveyInstruction) {
        const languageCode = this.surveyFacade.getSurveyLanguage();
        /**
         * Fix for PAAS-6506:
         * Find the survey question and get the answer id
         * When the Instruction type is SET_DATA_PROP and the prop value is the question id
         * That question indicates a yes/no question
         */
        let response = new SeededSurveyResponse(instruction.data.prop, instruction.data.value, languageCode);
        const surveyQuestion = this.surveyFacade.getItemByIdAndType(instruction.data.prop, SurveyElementType.SURVEY_QUESTION);
        const question = get(surveyQuestion, 'question');
        if (surveyQuestion && question) {
            // const answerId
            const keyId = typeof instruction.data.value === 'boolean' && instruction.data.value ? 'yesId' : 'noId';
            const answerId = get(question, `data.${keyId}`);
            response = new YesNoSurveyResponse(surveyQuestion, instruction.data.value, answerId, null, languageCode);
        }
        this.store.dispatch(new RecordResponseAction(response));
    }

    private openExternalLink(instruction: SurveyInstruction): void {
        // get the link data
        const link = get(instruction, 'data.link');
        if (link) {
            // dispatch an event to log the clicked link
            const event = new LinkClicked(link);
            this.store.dispatch(new EventTrackAction(event));
            // actually visit the link
            window.open(link, '_blank');
        }
    }

    private loadBlockById(instruction: SurveyInstruction): void {
        const ids = this.responsesFacade.gePendingResponseIds();

        if (ids.length > 0) {
            // Clear previous interval if still active
            this.pendingCheckSub?.unsubscribe();

            this.store.dispatch(new LoadingAction());
            // Poll every 500ms until pending responses are empty
            this.pendingCheckSub = interval(500)
                .pipe(takeWhile(() => this.responsesFacade.gePendingResponseIds().length > 0, true))
                .subscribe(() => {
                    const currentIds = this.responsesFacade.gePendingResponseIds();
                    if (currentIds.length === 0) {
                        console.log('No pending responses left — activating block.');
                        this.pendingCheckSub?.unsubscribe();

                        this.store.dispatch(new DoneLoadingAction());
                        this.store.dispatch(new ActivateBlockAction(instruction.data));
                    }
                });
        } else {
            this.store.dispatch(new ActivateBlockAction(instruction.data));
        }
    }

    private printElement(instruction: SurveyInstruction): void {
        const blockId = this.blocksFacade.getActiveId();
        const payload = {
            eventType: 'CLICK',
            action: 'PRINT',
            context: { blockId }
        }
        this.store.dispatch(new SurveyTrackingAction(payload));
        const selector = instruction.data || '.main-view';
        const element = document.querySelector(selector);
        if (!element) {
            console.warn('Element not found for selector:', selector);
            return;
        }
        // Add class to body to hide everything except print area
        document.body.classList.add('hide-body');
        
        const printClass = 'print-area';
        element.classList.add(printClass);
        window.print();
        setTimeout(() => {
            element.classList.remove(printClass);
            document.body.classList.remove('hide-body');
        }, 1000);
    }
}
